import React, { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import useRefreshToken from 'hooks/useRefreshToken';
import PrivateLayout from 'components/layout/PrivateLayout';
import BookingOnlineV2 from 'features/booking/BookingOnlineV2';
import BookingDepositTransaction from './booking/DepositTransaction';
import CouponVerifyOtpPage from './coupon/verify-otp';
import BookingOnlineV3 from './booking/BookingOnlineV3';
import BookingOnlineV4 from './booking/BookingOnlineV4';
import FeedbackPage from './review/feedback';

// Lazy load các component
const GiftCardDetail = lazy(() => import('./GiftCardDetail'));
const NotFoundPage = lazy(() => import('./NotFound'));
const BookingOnlinePage = lazy(() => import('./booking'));
const BookingCompleted = lazy(() => import('./booking/BookingCompleted'));
const BookingDetail = lazy(() => import('./booking/Detail'));
const CheckInShieldLayout = lazy(() => import('./checkIn/pages/CheckInShieldLayout'));
const ChooseServicesCheckIn = lazy(() => import('./checkIn/pages/ChooseServices'));
const ChooseTypeCheckIn = lazy(() => import('./checkIn/pages/ChooseTypeCheckIn'));
const InfoCustomer = lazy(() => import('./checkIn/pages/InfoCustomer'));
const CouponPage = lazy(() => import('./coupon'));

const CouponReceivePage = lazy(() => import('./coupon/new-customer/InputsPage'));
const CouponReceiveV2Page = lazy(() => import('./coupon/receive-v2'));
const CouponReceiveDetailPage = lazy(() => import('./coupon/new-customer/DetailPage'));
const CouponReceiveReferralPage = lazy(() => import('./coupon/new-customer/ReferralPage'));
const CouponReceiveReferralIntroPage = lazy(() => import('./coupon/new-customer/ReferralIntroPage'));
const CouponReceiveReferralSuccessPage = lazy(() => import('./coupon/new-customer/DetailPage/ReferralSuccess'));

const CouponReferralPage = lazy(() => import('./coupon/referral/InputsPage'));
const CouponReferralInvitePage = lazy(() => import('./coupon/referral/InvitePage'));

const ReceiptPage = lazy(() => import('./receipt'));
const BuyGiftCardPage = lazy(() => import('./shopping/GiftCard/BuyGiftCard'));
const BuyGiftCardCompleted = lazy(() => import('./shopping/GiftCard/BuyGiftCardCompleted'));
const GiftCardListPage = lazy(() => import('./shopping/GiftCard/GiftCardList'));
const ChooseStaffCheckIn = lazy(() => import('./checkIn/pages/ChooseStaff'));
const SuccessCheckIn = lazy(() => import('./checkIn/pages/Success'));
const CreditPayment = lazy(() => import('./payment/pages/CreditPayment'));
const CreditPaymentSuccess = lazy(() => import('./payment/pages/Success'));
const CreditPaymentFail = lazy(() => import('./payment/pages/Fail'));
const ConfirmSuccessCus = lazy(() => import('./payment/pages/ConfirmSuccess'));
const WrappingPayment = lazy(() => import('./payment/components/WrappingPayment'));
const ReviewDetail = lazy(() => import('./review'));
const SalonSummaryReport = lazy(() => import('./report/SalonSummaryReport'));
const CustomerReferral = lazy(() => import('./customer/CustomerReferral'));

const ServicesMenu = lazy(() => import('./service_menu'));

const RootScreens = () => {
  const { shield } = useRefreshToken();

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="store/:shop_id" element={shield(PrivateLayout)}>
          <Route path="bill/:bill_id">
            <Route path="receipt" element={<ReceiptPage />} />
          </Route>
          <Route path="review/:review_id" element={<ReviewDetail />} />

          <Route path="coupon/:coupon_id" element={<CouponPage />} />

          <Route path="coupon-receive-v2" element={<CouponReceiveV2Page />} />
          <Route path="coupon-verify-otp" element={<CouponVerifyOtpPage />} />
          <Route path="coupon-receive" element={<CouponReceivePage />} />
          <Route path="coupon-receive/referral-intro" element={<CouponReceiveReferralIntroPage />} />
          <Route path="coupon-receive/referral" element={<CouponReceiveReferralPage />} />
          <Route path="coupon-receive/referral/success" element={<CouponReceiveReferralSuccessPage />} />
          <Route path="coupon-receive/success" element={<CouponReceiveDetailPage />} />

          <Route path="coupon-referral" element={<CouponReferralPage />} />
          <Route path="coupon-referral/invite" element={<CouponReferralInvitePage />} />
          <Route path="coupon-referral/success" element={<CouponReceiveReferralSuccessPage mode='referral' />} />

          <Route path="booking">
            <Route path="detail/:booking_id" element={<BookingDetail />} />
            <Route path="online" element={<BookingOnlinePage />} />
            <Route path="online/completed" element={<BookingCompleted />} />
          </Route>
          <Route path="booking-v2" >
            <Route path="" element={<BookingOnlineV2 />} />
            <Route path="deposit-transaction/:trans_id" element={<BookingDepositTransaction />} />
          </Route>
          <Route path="booking-online" >
            <Route path="" element={<BookingOnlineV4 />} />
            <Route path="deposit-transaction/:trans_id" element={<BookingDepositTransaction />} />
          </Route>
          <Route path="booking-v3" >
            <Route path="" element={<BookingOnlineV3 />} />
            <Route path="deposit-transaction/:trans_id" element={<BookingDepositTransaction />} />
          </Route>
          <Route path="shopping/gift-card" element={<GiftCardListPage />} />
          <Route path="gift-card/:id" element={<GiftCardDetail />} />
          <Route path="shopping/gift-card/:id">
            <Route path="" element={<BuyGiftCardPage />} />
            <Route path="completed" element={<BuyGiftCardCompleted />} />
          </Route>
          <Route path="payment/:ticketId" element={<WrappingPayment />}>
            <Route path="" element={<CreditPayment />} />
            <Route path="add-tip">
              <Route path="" element={<CreditPaymentSuccess />} />
              <Route path="success" element={<ConfirmSuccessCus />} />
            </Route>
            <Route path="fail" element={<CreditPaymentFail />} />
          </Route>
          <Route path='check-in/:station_number' element={<InfoCustomer />} />
          <Route path='check-in/:station_number' element={<CheckInShieldLayout />}>
            <Route path='choose-type' element={<ChooseTypeCheckIn />} />
            <Route path='choose-service' element={<ChooseServicesCheckIn />} />
            <Route path='choose-staff' element={<ChooseStaffCheckIn />} />
            <Route path='success/:id/:type' element={<SuccessCheckIn />} />
          </Route>
          <Route path='customer/:customer_code' element={<CustomerReferral />} />
          <Route path='services-menu' element={<ServicesMenu />} />
          <Route path="feedback/:billId" element={<FeedbackPage />} />
        </Route>
        <Route path='store/:shop_id/report/summary' element={<SalonSummaryReport />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default RootScreens;